import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const CustomSelect = ({ value, onChange, children, className = '', style = {}, maxWidth = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 160, openUp: false });
  const containerRef = useRef(null);

  const options = React.Children.toArray(children).filter(
    child => child.type === 'option'
  );

  const selectedLabel = options.find(opt => opt.props.value === value)?.props.children || '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        !(e.target.closest && e.target.closest('[data-customselect-dropdown]'))
      ) {
        setIsOpen(false);
      }
    };
    const handleReposition = (e) => {
      if (e.target && e.target.closest && e.target.closest('[data-customselect-dropdown]')) return;
      setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220 && rect.top > 220;
      setCoords({
        top: openUp ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 160),
        openUp,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (val) => {
    onChange({ target: { value: val } });
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative inline-flex ${className}`} style={maxWidth ? { maxWidth } : undefined}>
      <button
        type="button"
        onClick={handleToggle}
        className={`w-full flex items-center justify-between gap-1.5 outline-none transition-colors whitespace-nowrap ${className}`}
        style={style}
      >
        <span className="truncate">{selectedLabel || 'Seleccionar...'}</span>
        <svg className={`w-3.5 h-3.5 opacity-60 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          data-customselect-dropdown
          className="fixed z-[10000] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
          style={{
            left: coords.left,
            width: coords.width,
            maxWidth: 320,
            ...(coords.openUp ? { bottom: window.innerHeight - coords.top } : { top: coords.top }),
          }}
        >
          <div className="max-h-[220px] overflow-y-auto overflow-x-hidden slim-scrollbar py-1">
            {options.map(opt => {
              const val = opt.props.value;
              const label = opt.props.children;
              const isSelected = value === val;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => handleSelect(val)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    isSelected
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomSelect;
